import React, { useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowRightIcon, ArrowTopRightOnSquareIcon, CheckIcon, EnvelopeIcon, InboxArrowDownIcon, MegaphoneIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import useDocumentTitle from '../hooks/useDocumentTitle'
import usePomiarRynku from '../hooks/usePomiarRynku'
import { useAuth } from '../context/AuthContext'
import * as api from '../api/rynek'
import FormularzOgloszenia from '../components/rynek/FormularzOgloszenia'
import RynekFirmy from '../components/rynek/RynekFirmy'
import { kodOgloszenia, kontoSzkicu, odczytajSzkic, wyczyscSzkic, zapiszSzkic } from '../lib/rynek'
import { odczytajWyborDoboru, odczytajZapisDoboru } from '../lib/doborCele'

/**
 * Rynek `/rynek`: „Wystaw zlecenie na Rynku” (decyzje właściciela 2026-10-21, `config/rynek.js`; od 2026-10-24
 * formularz i teksty według makiety „VoltValue dla domu” 12, w tekstach dla klienta „wykonawca”).
 *
 * Wejścia: wprost (menu „Narzędzia”), z potwierdzenia zestawu i z raportu „Sprawdź ofertę” (`state.ogloszenie`
 * z gotowym sprzętem). Gość wypełnia całe zlecenie bez konta (decyzja właściciela 2026-10-26): konto zakłada w oknie
 * „Wystaw zlecenie”, a jeden mail potwierdza konto i publikuje zlecenie (ekran „Sprawdź skrzynkę”). Szkic czeka w pamięci
 * przeglądarki (`lib/rynek`) i przechodzi na konto po zalogowaniu.
 *
 * Konto firmowe widzi tu „Rynek ofert” (ogłoszenia pokazane jego firmie, decyzja 2026-10-21), chyba że przyszło
 * z gotowym sprzętem. Własnego zlecenia firma nie wystawia (decyzja 2026-10-25).
 */

const KROKI = [
  { Ikona: MegaphoneIcon, tytul: 'Wystawiasz zlecenie', opis: 'Za darmo, z kontem. Wybierasz, czy chcesz 3, 6 czy 9 ofert.' },
  { Ikona: InboxArrowDownIcon, tytul: 'Wykonawcy składają oferty', opis: 'W 48 godzin. Pokazujemy Ci najlepiej dopasowanych.' },
  { Ikona: UserGroupIcon, tytul: 'Ty wybierasz wykonawcę', opis: 'Adres i kontakt dostają tylko ci, których zaznaczysz.' },
]

const Rynek = () => {
  useDocumentTitle('Rynek: wystaw zlecenie na fotowoltaikę')
  const location = useLocation()
  const { isAuthenticated, firmowe, profil, loading, profilUstalany } = useAuth()
  // Logowanie w oknie „Wystaw zlecenie” (gość, 2026-10-26) na chwilę ustala profil od nowa: formularz ma wtedy zostać
  // z wpisanymi danymi, a nie zniknąć za „Wczytuję…”.
  const formularzWidoczny = useRef(false)

  // Zanim profil się wczyta, nie wiadomo, czy to konto firmowe: bez tego firma przez chwilę widziała widok klienta.
  if ((loading || (isAuthenticated && profilUstalany)) && !formularzWidoczny.current) return <p className="mx-auto max-w-3xl px-4 py-12 text-sm text-gray-500">Wczytuję…</p>
  // Konto firmowe widzi „Rynek ofert”, bez własnego zlecenia (decyzja właściciela 2026-10-25, uwagi przeglądu E2).
  // Formularz tylko z gotowym sprzętem z wejścia (zestaw, raport oferty), nigdy z samego szkicu w przeglądarce: ten mógł
  // zostać po kliencie na tym samym komputerze (D12).
  const zWejscia = location.state?.ogloszenie || null
  const zSprzetem = Boolean(zWejscia?.opis && Object.keys(zWejscia.opis).length)
  if (firmowe && !zSprzetem) { formularzWidoczny.current = false; return <RynekFirmy /> }
  formularzWidoczny.current = true
  return <RynekKlienta zWejscia={zWejscia} isAuthenticated={isAuthenticated} konto={kontoSzkicu(isAuthenticated, profil)} />
}

const RynekKlienta = ({ zWejscia, isAuthenticated, konto }) => {
  const [poczatkowe] = useState(() => {
    // Zestaw z asystenta przychodzi bez celów i budżetu: te czekają w wyborze prostego doboru (P4, ważny 3 dni).
    const dobor = odczytajWyborDoboru(Date.now(), konto)
    if (zWejscia) {
      const dane = dobor && zWejscia.zrodlo === 'zestaw' ? { ...dobor, ...zWejscia } : zWejscia
      zapiszSzkic(dane, Date.now(), konto)
      return dane
    }
    // Szkic gościa przechodzi na konto po zalogowaniu; szkic innego konta się nie pokazuje (D12).
    const szkic = odczytajSzkic(Date.now(), konto)
    if (szkic && konto) zapiszSzkic(szkic, Date.now(), konto)
    return szkic
  })
  // Odpowiedzi z doboru jako źródło opisu także przy wejściu wprost (F9: „Moje odpowiedzi z doboru”).
  const [zapisDoboru] = useState(() => odczytajZapisDoboru(Date.now(), konto))
  const [wyslane, setWyslane] = useState(null)
  // Czas, przewinięcie i pierwsze kliknięcie na ścieżce /rynek (pomiar anonimowy, 2026-10-28).
  const tresc = useRef(null)
  usePomiarRynku(tresc)

  return (
    <div ref={tresc} className="vv-dom bg-gray-50 text-gray-900">
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:px-6 sm:pt-12">
        <header className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />Nikt nie dzwoni bez Twojej zgody
            </span>
            {isAuthenticated && (
              <Link to="/rynek/moje" className="text-sm font-semibold text-blue-700 no-underline hover:underline">Moje zlecenia</Link>
            )}
          </div>
          <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.02em] sm:text-[42px]">Wystaw zlecenie na Rynku</h1>
          <p className="max-w-xl text-base leading-7 text-gray-600">
            Opisz, czego potrzebujesz. Zapraszamy wykonawców z okolicy, którzy pasują do zlecenia, a Ty wybierasz, kto dostanie adres i kontakt.
          </p>
        </header>

        <ol className="mt-7 grid list-none gap-4 sm:grid-cols-3">
          {KROKI.map(({ Ikona, tytul, opis }, i) => (
            <li key={tytul} className="flex items-start gap-3">
              <span aria-hidden="true" className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-blue-600">
                <Ikona className="h-5 w-5" />
              </span>
              <span className="flex flex-col">
                <span className="text-[13px] font-semibold">{i + 1}. {tytul}</span>
                <span className="text-xs leading-[18px] text-gray-500">{opis}</span>
              </span>
            </li>
          ))}
        </ol>

        <section aria-label="Zlecenie" className="karta" style={{ marginTop: 32 }}>
          <div className="karta-glowa">
            <span className="karta-nazwa"><MegaphoneIcon className="h-4 w-4" aria-hidden="true" />Wystaw zlecenie na Rynku</span>
          </div>
          {wyslane?.stan === 'czeka_na_email' ? (
            <SprawdzSkrzynke email={wyslane.email} />
          ) : wyslane ? (
            <div className="wynik" aria-live="polite">
              <span className="wynik-ikona"><CheckIcon className="h-6 w-6" strokeWidth={2.4} aria-hidden="true" /></span>
              <h2 className="text-[22px] font-bold leading-snug">Zlecenie wystawione</h2>
              <p>
                {wyslane.tryb_kontaktu === 'przetarg'
                  ? `Zapraszamy wszystkich pasujących wykonawców z Twojej okolicy. Za 48 godzin pokażemy Ci ${wyslane.liczba_firm} najlepiej dopasowanych spośród tych, którzy przyjęli Twoje warunki, razem z ich ofertami. Twojego adresu i kontaktu nie dostanie nikt, dopóki ich nie przekażesz.`
                  : wyslane.tryb_kontaktu === 'dopasowanie'
                    ? 'Dobierzemy do niego najwyżej 3 wykonawców z Twojej okolicy. Napiszemy, którzy to wykonawcy, a oni zadzwonią z ofertą.'
                    : 'Pokażemy je wykonawcom z Twojej okolicy. Gdy któryś odpowie, dostaniesz e-mail, a oferty zobaczysz w „Moich zleceniach”.'}
              </p>
              <div className="rzad">
                <Link to={`/rynek/moje/${kodOgloszenia(wyslane)}`} className="btn btn-glowny btn-m">
                  Zobacz zlecenie<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
                </Link>
              </div>
            </div>
          ) : (
            <FormularzOgloszenia poczatkowe={poczatkowe} dobor={zapisDoboru?.dane || null} onWyslane={(d) => { wyczyscSzkic(); setWyslane(d) }} />
          )}
        </section>
      </div>
    </div>
  )
}

/**
 * Zlecenie czeka na potwierdzenie e-maila (gość z `POST /ogloszenia/z-kontem` albo konto z niepotwierdzonym adresem):
 * jeden mail potwierdza konto i publikuje zlecenie. „Wyślij ponownie” prosi o nowy link (`/ogloszenia/z-kontem/ponow`).
 */
const SprawdzSkrzynke = ({ email }) => {
  const [ponow, setPonow] = useState({ busy: false, tekst: '' })
  const wyslijPonownie = async () => {
    setPonow({ busy: true, tekst: '' })
    const r = await api.ponowPotwierdzenie(email)
    setPonow({ busy: false, tekst: r.ok ? 'Jeśli zlecenie czeka na potwierdzenie, wysłaliśmy nowy link.' : r.error })
  }
  return (
    <div className="wynik" aria-live="polite">
      <span className="wynik-ikona"><EnvelopeIcon className="h-6 w-6" strokeWidth={2} aria-hidden="true" /></span>
      <h2 className="text-[22px] font-bold leading-snug">Sprawdź skrzynkę</h2>
      <p>
        Wysłaliśmy link na <b>{email}</b>. Potwierdź e-mail, a zlecenie trafi do wykonawców z Twojej okolicy.
        Do tego czasu nikt go nie widzi.
      </p>
      <div className="rzad">
        {[['Otwórz Gmail', 'https://mail.google.com'], ['Otwórz Outlook', 'https://outlook.live.com']].map(([nazwa, href]) => (
          <a key={nazwa} href={href} target="_blank" rel="noopener noreferrer" className="btn btn-obrys btn-m">
            {nazwa}<ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        ))}
        <button type="button" className="link" onClick={wyslijPonownie} disabled={ponow.busy}>
          {ponow.busy ? 'Wysyłam…' : 'Wyślij ponownie'}
        </button>
      </div>
      {ponow.tekst && <p className="pole-podpowiedz">{ponow.tekst}</p>}
    </div>
  )
}

export default Rynek
